import { useState, useRef, useEffect, lazy, Suspense } from "react"
import { Plus, Loader2, X, Check, ChevronDown } from "lucide-react"
import { DesignOptionsMenu } from "./design-options-menu"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { MissingParametersError } from "./error-message"
import { BubbleVisualizer } from "./bubble-visualizer"
import type { ArchitectureStyle } from "./architecture-style-modal"
import type { InspirationImage } from "./inspiration-images-modal"
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider"

/**
 * Widget Container Implementation Notes:
 *
 * Collections for Color Families and Inspiration Images:
 * - Users can build collections of saved color families and inspiration images
 * - Only one color and one inspiration can be active at a time
 * - Active selections are highlighted with a purple border
 * - Users can click between saved options to quickly switch styles
 * - "Add More" button allows expanding the collection
 */
import {
  getUserCredits,
  uploadAssetFromUrl,
  renovateImage,
  saveDesign,
  deleteDesign,
  getWidgetConfig,
  type CreditDetails,
  type Asset,
  type WidgetConfigItem,
  type ColorFamilyWithHouses,
  type DetectedObject
} from "@/lib/api-service"

// Lazy load modals since they're not needed on initial render
const ArchitectureStyleModal = lazy(() => import("./architecture-style-modal").then(module => ({ default: module.ArchitectureStyleModal })))
const InspirationImagesModal = lazy(() => import("./inspiration-images-modal").then(module => ({ default: module.InspirationImagesModal })))
const KeywordModal = lazy(() => import("./keyword-modal").then(module => ({ default: module.KeywordModal })))
const SaveDesignModal = lazy(() => import("./save-design-modal").then(module => ({ default: module.SaveDesignModal })))
const PreserveObjectsModal = lazy(() => import("./preserve-objects-modal").then(module => ({ default: module.PreserveObjectsModal })))

interface BubbleStyle {
  id: string
  name: string
  image: string
  type?: "architecture" | "color" | "inspiration" | "keyword"
  architectureData?: ArchitectureStyle
  code?: string
}

interface SavedDesign {
  id: string
  name: string
  originalImageUrl: string
  generatedImageUrl: string
  timestamp: number
}

interface PreservedObject {
  originalObject: string;
  name: string;
}

interface WidgetContainerProps {
  default_renovation: boolean;
  style_codes: string[];
  color_code?: string;
  user_image_url?: string; // Still optional in the type, but we'll validate it
  inspiration_code?: string;
  user_id?: string; // Still optional in the type, but we'll validate it
  apiKey?: string; // Deprecated, use client_key instead
  client_key?: string; // Required, but still optional in type for backward compatibility
  photo_angle?: 'front_view' | 'rear_view' | 'left_view' | 'right_view';
  upscale_resolution?: number; // Upscale resolution parameter (1x, 2x, 3x, 4x, 5x)
  extra?: Record<string, string | number>; // Extra parameters for project_id, tmp_project_id, etc.
}


export default function WidgetContainer({
  default_renovation = false,
  style_codes = [],
  color_code,
  user_image_url,
  inspiration_code,
  user_id,
  apiKey,
  client_key, // Dzinly client key
  photo_angle = 'front_view',
  upscale_resolution = 3, // Default to 1x upscale resolution
  extra = {} // Default to empty object for extra parameters
}: WidgetContainerProps) {
  // For backward compatibility, use client_key as the main API key
  const effectiveApiKey = client_key || apiKey || '';
  // Define valid client keys
  const VALID_CLIENT_KEYS = ['pk_ZQU9soOQ0pVW9r3MRdpmcQbo7btGc2dUGVMEsqj'];

  // Check for mandatory parameters
  const missingParams: string[] = [];
  if (!user_id) missingParams.push('user_id');
  if (!user_image_url) missingParams.push('user_image_url');
  if (!client_key) missingParams.push('client_key');
  else if (!VALID_CLIENT_KEYS.includes(client_key)) missingParams.push('client_key'); // Invalid client key
  // For backward compatibility
  const style_ids = style_codes;
  const color_ids = color_code ? [color_code] : [];

  // Handle user_image_url and inspiration_code for backward compatibility
  let image_urls: string[] = [];
  if (user_image_url) {
    image_urls.push(user_image_url);
  }

  // Initialize all state variables first
  const [centerBubble, setCenterBubble] = useState<BubbleStyle>({
    id: "original",
    name: "Original Design",
    image: "/modern-house-exterior.webp",
  })

  const [bubbleStyles, setBubbleStyles] = useState<BubbleStyle[]>([])
  const [creditDetails, setCreditDetails] = useState<CreditDetails | null>(null)
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false)
  const [isInspirationModalOpen, setIsInspirationModalOpen] = useState(false)
  const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false)
  const [isSaveDesignModalOpen, setIsSaveDesignModalOpen] = useState(false)
  const [selectedStyleType, setSelectedStyleType] = useState<"architecture" | "color" | "inspiration" | "keyword">(
    "architecture",
  )

  // Collection for inspiration images only
  // Color families section has been removed from UI
  const [inspirationCollection, setInspirationCollection] = useState<InspirationImage[]>([])
  const [selectedInspirationId, setSelectedInspirationId] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [showComparison, setShowComparison] = useState(false)
  const [savedDesigns, setSavedDesigns] = useState<SavedDesign[]>([])
  const [activeDesignId, setActiveDesignId] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isPreserveObjectsModalOpen, setIsPreserveObjectsModalOpen] = useState(false)
  const [preservedObjects, setPreservedObjects] = useState<PreservedObject[]>([])
  const [widgetStyles, setWidgetStyles] = useState<WidgetConfigItem[]>([])
  const [widgetColorFamiliesWithHouses, setWidgetColorFamiliesWithHouses] = useState<ColorFamilyWithHouses[]>([])
  const [widgetInspirationHouses, setWidgetInspirationHouses] = useState<WidgetConfigItem[]>([])
  const [isLoadingConfig, setIsLoadingConfig] = useState(false)
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const [selectedColorFamily, setSelectedColorFamily] = useState<string>('all')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const didFetchConfig = useRef(false)

  // Loading messages that rotate during generation
  const loadingMessages = [
    "We are analyzing everything!",
    "OK, everything is looking good! We are now generating ideas.",
    "Final design is now underway!",
    "Almost done! It is shaping up nicely.",
    "Your design is nearly complete.",
    "Adding final touches—your design will be ready any second!"
  ]

  // Dropdown functions
  const getSelectedFamilyName = () => {
    if (selectedColorFamily === 'all') return 'All Categories'
    const family = widgetColorFamiliesWithHouses.find(f => f.code === selectedColorFamily)
    return family ? family.name : 'All Categories'
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  // State for tracking API uploads and renovations
  const [originalImageUrl, setOriginalImageUrl] = useState(
    ""
  )
  const [generatedImageUrl, setGeneratedImageUrl] = useState("")
  const [uploadedAsset, setUploadedAsset] = useState<Asset | null | undefined>(null)
  const [isUploadingAsset, setIsUploadingAsset] = useState(false)
  const [generationError, setGenerationError] = useState<string | null>(null)

  // Add inspiration_code to bubbles after widget config is loaded
  useEffect(() => {
    try {
      // Skip if widget config is still loading or no inspiration_code is specified
      if (isLoadingConfig || !inspiration_code) {
        return;
      }

      // Find the matching inspiration house in the new color families structure
      let matchingInspiration = null;
      let familyCode = null;

      // Search through all color families and their inspirational houses
      if (widgetColorFamiliesWithHouses && Array.isArray(widgetColorFamiliesWithHouses)) {
        for (const colorFamily of widgetColorFamiliesWithHouses) {
          if (colorFamily.inspirational_houses && Array.isArray(colorFamily.inspirational_houses)) {
            const house = colorFamily.inspirational_houses.find(h => h.code === inspiration_code);
            if (house) {
              matchingInspiration = house;
              familyCode = colorFamily.code;
              break;
            }
          }
        }
      }

      // Fallback to legacy widgetInspirationHouses if not found in new structure
      if (!matchingInspiration && widgetInspirationHouses && widgetInspirationHouses.length > 0) {
        const legacyMatch = widgetInspirationHouses.find(house => house.code === inspiration_code);
        if (legacyMatch) {
          matchingInspiration = legacyMatch;
        }
      }

      if (matchingInspiration) {
        // Inspiration code matched

        // If we found it in a specific color family, automatically set that category
        if (familyCode) {
          setSelectedColorFamily(familyCode);
        }

        // Add to inspiration collection
        setInspirationCollection(prev => {
          // Check if it already exists
          if (!prev.some(i => i.id === matchingInspiration.code)) {
            return [...prev, {
              id: matchingInspiration.code,
              name: matchingInspiration.name,
              url: matchingInspiration.image
            }];
          }
          return prev;
        });

        // Set as selected inspiration
        setSelectedInspirationId(matchingInspiration.code);

        // Add it to bubble styles
        setBubbleStyles(prev => {
          // Avoid duplicates
          if (!prev.some(b => b.type === "inspiration" && b.id === `inspiration-${matchingInspiration.code}`)) {
            return [...prev, {
              id: `inspiration-${matchingInspiration.code}`,
              name: matchingInspiration.name,
              image: matchingInspiration.image,
              type: "inspiration" as const,
              code: matchingInspiration.code,
            }];
          }
          return prev;
        });
      } else {
        // No matching inspiration found
        console.log('No matching inspiration found for code:', inspiration_code);
      }
    } catch (error) {
      console.error('Error processing inspiration code:', error);
      // Continue without crashing
    }
  }, [inspiration_code, isLoadingConfig, widgetColorFamiliesWithHouses, widgetInspirationHouses]);



  // Update center bubble when original image URL changes
  useEffect(() => {
    setCenterBubble(prev => ({
      ...prev,
      image: originalImageUrl
    }));
  }, [originalImageUrl]);




  // Color families with inspirational houses for passing to modals
  const colorFamiliesWithHouses = widgetColorFamiliesWithHouses;


  // Function to fetch user credits
  const fetchUserCredits = async () => {
    if (user_id) {
      try {
        const details = await getUserCredits(effectiveApiKey, { userId: user_id });
        setCreditDetails({
          userId: details.user_id,
          designsLeft: details.credit_left,
          totalDesigns: details.credits,
          canDesign: details.can_design
        });
      } catch (error) {
        console.error("Failed to fetch credit details:", error);
        // Fallback
        setCreditDetails({
          userId: user_id || '',
          designsLeft: 3,
          totalDesigns: 3,
          canDesign: true
        });
      }
    } else {
      // Default for testing
      setCreditDetails({
        userId: '',
        designsLeft: 3,
        totalDesigns: 3,
        canDesign: true
      });
    }
  };

  // Initialize credit details and widget configuration
  useEffect(() => {
    const fetchWidgetConfig = async () => {
      setIsLoadingConfig(true);
      try {
        const config = await getWidgetConfig(effectiveApiKey);
        if (config.success && config.config) {
          // Handle the nested structure where items are in a 'result' array
          setWidgetStyles(config.config.styles?.result || []);
          setWidgetColorFamiliesWithHouses(config.config.colorFamiliesWithInspirationalHouses || []);
          // For backward compatibility, still set inspirational houses from the legacy field
          setWidgetInspirationHouses(config.config.inspirationalHouses?.result || []);
          // Widget configuration loaded successfully



          if(config?.existingProjectDetails){
            setSavedDesigns(config.existingProjectDetails.savedRenovations || [])
            setUploadedAsset(config.existingProjectDetails?.initialUploadedAsset)
            setOriginalImageUrl(config.existingProjectDetails?.initialUploadedAsset?.preview_url as string)

            // Handle custom inspiration image from existing project details
            if (config.existingProjectDetails.inspirationImageUrls?.length) {
              const processExistingCustomInspirationImage = () => {
                try {
                  // Upload the image as an asset

                  // Create a unique ID for the custom inspiration
                  // Add to inspiration collection
                  setInspirationCollection(prev => {
                    // Check if it already exists
                    if (config.existingProjectDetails?.inspirationImageUrls && Array.isArray(config.existingProjectDetails.inspirationImageUrls)) {
                      const newInspirations = config.existingProjectDetails.inspirationImageUrls.filter(url => !prev.some(i => i.url === url)).map((url, index) => ({
                      id: `existing-custom-inspiration-${Date.now()}-${index}`,
                      name: "Inspiration Image",
                      url: url as string
                      }));
                      return [...prev, ...newInspirations];
                    }
                    return prev;
                  });


                } catch (error) {
                  console.error('Error processing custom inspiration image from existing project:', error);
                }
              };

              processExistingCustomInspirationImage();
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch widget configuration:", error);
        // Fallbacks are built into the API service
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchUserCredits();

    if(!didFetchConfig.current){
    fetchWidgetConfig();
      didFetchConfig.current = true; // Mark that we've fetched the config
    }
  }, [effectiveApiKey, user_id]);

  // Listen for credits updated message from parent window
  useEffect(() => {
    const handleCreditsUpdated = (event: MessageEvent) => {
      if (event.data && event.data.type === 'credits_updated') {
        // Received credits_updated message
        fetchUserCredits();
      }
    };

    window.addEventListener('message', handleCreditsUpdated);

    return () => {
      window.removeEventListener('message', handleCreditsUpdated);
    };
  }, []);

  // Track if we've already processed various initialization steps
  const hasProcessedUrlParams = useRef(false);
  const hasRunDefaultRenovation = useRef(false);

  // Process prefilled styles from URL parameters - only once after widget config is loaded
  useEffect(() => {
    // Only process if widget config is loaded and we haven't processed yet
    if (isLoadingConfig || hasProcessedUrlParams.current) {
      return;
    }

    // Only proceed if we have data from the widget config
    if (widgetStyles.length === 0 && widgetColorFamiliesWithHouses.length === 0) {
      return;
    }

    // Mark that we've processed the URL parameters to avoid duplicate processing
    hasProcessedUrlParams.current = true;
    // Processing URL parameters with loaded widget config

    const initStyles = async () => {
      // Process style_ids
      if (style_ids.length > 0) {
        // Convert style_ids to bubble styles using ONLY loaded widget config
        const newStyles = style_ids.map(id => {
          // Find the style in the loaded widget config
          const configStyle = widgetStyles.find(s => s.code.toLowerCase() === id.toLowerCase());

          // If not found, use a default
          if (!configStyle) {
            // Style ID not found in widget config, using default
            return {
              id: `architecture-${id}`,
              name: `Style ${id}`,
              image: "/placeholder.svg?height=80&width=80",
              type: "architecture" as const,
              architectureData: {
                id: id,
                name: `Style ${id}`,
                preview_image: "/placeholder.svg?height=80&width=80"
              }
            };
          }

          return {
            id: `architecture-${configStyle.code}`,
            name: configStyle.name,
            image: configStyle.image,
            type: "architecture" as const,
            architectureData: {
              id: configStyle.code,
              name: configStyle.name,
              preview_image: configStyle.image,
            },
            code: configStyle.code,
          };
        });

        setBubbleStyles(prev => [...prev, ...newStyles]);
      }

      // Note: color_ids processing removed as Color Families section is being removed

      // Process user_image_url - set it as the original image
/*       if (user_image_url && !originalImageUrl) {
        // Set the user image URL as the original image
        setOriginalImageUrl(user_image_url);


        // Pre-upload the asset if we have a URL and haven't already started uploading
        if (!hasInitializedUpload.current && user_image_url) {
          hasInitializedUpload.current = true; // Mark that we've started the upload process
          const preloadAsset = async () => {
            try {
              setIsUploadingAsset(true);
              setGenerationError(null);

              const asset = await uploadAssetFromUrl(user_image_url);
              setUploadedAsset(asset);
            } catch (error) {
              console.error('Error preloading asset:', error);
              setGenerationError('Failed to preload the original image. Please try again.');
              // Reset the flag if upload fails so we can try again
              hasInitializedUpload.current = false;
            } finally {
              setIsUploadingAsset(false);
            }
          };

          preloadAsset();
        }
      } */

      // Check if we have a custom inspiration image in extra parameters
    const firstCustomInspirationImage = inspirationCollection?.[0]

    console.log('First custom inspiration images:', inspirationCollection);

    if (firstCustomInspirationImage ) {
      // Process custom inspiration image
      const processCustomInspirationImage = async () => {
        try {
          // Set as selected inspiration
          setSelectedInspirationId(firstCustomInspirationImage?.id);

          // Add it to bubble styles
          setBubbleStyles(prev => {
            // Avoid duplicates
            if (!prev.some(b => b.type === "inspiration" && (b.image === firstCustomInspirationImage?.url))) {
              return [...prev, {
                id: firstCustomInspirationImage?.id,
                name: firstCustomInspirationImage?.name || "Inspiration Image",
                image: firstCustomInspirationImage?.url,
                type: "inspiration" as const,
              }];
            }
            return prev;
          });

        } catch (error) {
          console.error('Error processing custom inspiration image:', error);
        }
      };

      processCustomInspirationImage();
    }

      // If default_renovation is true, trigger generation after asset is uploaded
      if (default_renovation && creditDetails?.canDesign) {
        // We'll handle this in a separate useEffect to ensure asset is loaded first
      }
    };

    if (style_ids.length > 0 || color_ids.length > 0 || user_image_url || image_urls.length > 0) {
      initStyles();
    }
  }, [
    // Only run when dependencies change AND we haven't processed URL params yet
    style_ids,
    color_ids,
    user_image_url,
    image_urls,
    isLoadingConfig,
    widgetStyles.length, // Only depend on the length to avoid excessive reruns
    widgetColorFamiliesWithHouses.length,
    widgetInspirationHouses.length
  ]);

  // Handle default renovation after asset is uploaded - only once
  useEffect(() => {

    const bubblesFilled = bubbleStyles?.filter(bubble => bubble.type === 'architecture' || bubble.type === 'color' || bubble.type === 'inspiration')?.length > 0;

    // Only run default renovation if we haven't done it yet
    if (default_renovation && creditDetails?.canDesign && uploadedAsset && !isUploadingAsset && !hasRunDefaultRenovation.current &&
      bubblesFilled
    ) {
      // Running default renovation

      // Mark that we've run the default renovation
      hasRunDefaultRenovation.current = true;

        handleGenerate({
          default_renovation: true
        });

    }
  }, [default_renovation, creditDetails, uploadedAsset, isUploadingAsset, bubbleStyles]);

  const handleAddStyle = () => {
    // Open the style selection modal
    setSelectedStyleType("inspiration")
    setIsInspirationModalOpen(true)
  }


  const handleSelectStyle = (style: ArchitectureStyle) => {
    // Check if this style already exists in bubbles
    const styleExists = bubbleStyles.some(
      bubble => bubble.type === "architecture" && bubble.architectureData?.id === style.id
    );

    if (styleExists) {
      // Style already added
      return; // Don't add duplicate styles
    }

    // Add the selected style to the bubbles
    const newId = `${style.id}-${Date.now()}`

    // Make sure the preview_image doesn't have quotes
    const cleanImageUrl = style.preview_image.replace(/^"|"$/g, "")

    setBubbleStyles([
      ...bubbleStyles,
      {
        id: newId,
        name: style.name,
        image: cleanImageUrl,
        type: selectedStyleType,
        architectureData: {
          ...style,
          preview_image: cleanImageUrl,
        },
        code: style.id,
      },
    ])

    // Return to bubble view if currently in comparison view
    if (showComparison) {
      setShowComparison(false)
    }
  }


  const handleSelectInspirationImage = (inspiration: InspirationImage) => {
    // Check if this inspiration image is already in the collection
    const imageExists = inspirationCollection.some(img => img.id === inspiration.id);

    // If not in collection, add it to the collection
    if (!imageExists) {
      setInspirationCollection(prev => [...prev, inspiration]);
    }

    // Set as selected inspiration
    setSelectedInspirationId(inspiration.id);

    // First remove any existing inspiration bubbles
    const filteredBubbles = bubbleStyles.filter(bubble => bubble.type !== "inspiration");

    // Add the new selected inspiration image
    const newId = `${inspiration.id}-${Date.now()}`

    setBubbleStyles([
      ...filteredBubbles,
      {
        id: newId,
        name: inspiration.name || "Inspiration",
        image: inspiration.url,
        type: "inspiration",
        code: inspiration.id,
      },
    ])

    // Return to bubble view if currently in comparison view
    if (showComparison) {
      setShowComparison(false)
    }
  }

  const handleRemoveBubble = (id: string) => {
    setBubbleStyles(bubbleStyles.filter((style) => style.id !== id))
  }



  // Make sure collections are updated when we select from modal
  useEffect(() => {
    // Find the active inspiration bubble
    const activeBubble = bubbleStyles.find(b => b.type === "inspiration");
    if (activeBubble) {
      // Find the matching inspiration in our collection by URL
      const matchingInspiration = inspirationCollection.find(i => i.url === activeBubble.image);
      if (matchingInspiration) {
        setSelectedInspirationId(matchingInspiration.id);
      }
    }
  }, [bubbleStyles, inspirationCollection]);

  // Handle selecting an inspiration image from the collection or color families
  const handleSelectInspirationFromCollection = (inspirationId: string) => {
    // First try to find the inspiration in the collection
    let inspiration = inspirationCollection.find(img => img.id === inspirationId);
    
    // If not found in collection, look in color families
    if (!inspiration && widgetColorFamiliesWithHouses) {
      for (const family of widgetColorFamiliesWithHouses) {
        const house = family.inspirational_houses.find(h => h.code === inspirationId);
        if (house) {
          inspiration = {
            id: house.code,
            name: house.name,
            url: house.image
          };
          break;
        }
      }
    }
    if (inspiration) {
      // Set as selected inspiration
      setSelectedInspirationId(inspirationId);

      // Update the bubble style
      const filteredBubbles = bubbleStyles.filter(bubble => bubble.type !== "inspiration");
      const newId = `${inspiration.id}-${Date.now()}`;

      setBubbleStyles([
        ...filteredBubbles,
        {
          id: newId,
          name: inspiration.name || "Inspiration",
          image: inspiration.url,
          type: "inspiration",
          code: inspiration.id,
        },
      ]);

      // Return to bubble view if currently in comparison view
      if (showComparison) {
        setShowComparison(false);
      }
    }
  }

  // Add a keyword bubble
  const handleAddKeyword = (keyword: string) => {
    if (keyword.trim()) {
      const newId = `keyword-${Date.now()}`
      setBubbleStyles([
        ...bubbleStyles,
        {
          id: newId,
          name: keyword,
          image: "/placeholder.svg?height=100&width=100",
          type: "keyword",
        },
      ])
    }
  }

  // Handle saving preserved objects
  const handleSavePreservedObjects = (objectIds: string[], objectData: DetectedObject[]) => {
    // Save the full objects data with names for display
    const objectsWithNames: PreservedObject[] = objectIds.map(id => {
      const matchingObject = objectData.find(obj => obj.originalObject === id);
      return {
        originalObject: id,
        name: matchingObject?.name || id // Fallback to ID if name not found
      };
    });

    setPreservedObjects(objectsWithNames);
    // Preserved objects saved
  }

  // Handle Generate button click
  const handleGenerate = async (params?:{
    default_renovation?: boolean
  }) => {
    // Check if user has credits left
    if (creditDetails && creditDetails.designsLeft === 0) {
      // No credits left, sending buy_more_credits message
      window.DzinlyWidget.sendMessage({
        type: 'buy_more_credits',
        data: {
          userId: creditDetails.userId
        }
      });
      return;
    }

    setIsGenerating(true)
    setGenerationError(null)
    setLoadingMessageIndex(0) // Reset loading message index

    // Set up interval to rotate loading messages every 10 seconds
    const messageInterval = setInterval(() => {
      setLoadingMessageIndex(prevIndex => {
        // Cycle through messages, but don't exceed array length
        const nextIndex = prevIndex + 1;
        return nextIndex < loadingMessages.length ? nextIndex : prevIndex;
      });
    }, 10000);

    try {
      // Step 1: Make sure we have an uploaded asset
      let asset = uploadedAsset

      if (!asset) {
        setIsUploadingAsset(true)
        try {
          asset = await uploadAssetFromUrl(originalImageUrl)
          setUploadedAsset(asset)
        } catch (error) {
          console.error('Error uploading asset:', error)
          setIsUploadingAsset(false)
          setIsGenerating(false)
          setGenerationError('Failed to upload the original image. Please try again.')
          return
        } finally {
          setIsUploadingAsset(false)
        }
      }

      // Step 2: We'll use the default style ID if no architecture bubbles are present
      const defaultStyleId = 'modern'

      // Step 3: Apply the renovation to the uploaded asset with all bubbles and preserved objects
      // Extract just the IDs from preservedObjects for the API
      const preservedObjectIds = preservedObjects.map(obj => obj.originalObject);

      // Use default_renovation from URL param if provided via params, otherwise false
      const isDefaultRenovation = params?.default_renovation || false

      const renovationResult = await renovateImage(
        asset.id,
        defaultStyleId,
        'renovate',
        preservedObjectIds,
        bubbleStyles,
        isDefaultRenovation,
        user_id,
        extra, // Pass the extra parameters to the API
        upscale_resolution // Pass the upscale resolution parameter
      )
      // Renovation successful

      // Step 4: Set the generated image URL
      setGeneratedImageUrl(renovationResult.result_image_url)

      // Step 5: Refetch credits since backend automatically consumes a credit
      if (user_id) {
        try {
          // Wait a short time to ensure the credit has been consumed on the backend
          await new Promise(resolve => setTimeout(resolve, 500));
          await fetchUserCredits();
        } catch (error) {
          console.error("Failed to refresh credit details:", error);
          // Still proceed with generation if we can't refresh credits
        }
      } else {
        // For demo without user ID
        setCreditDetails(prev => prev ? {
          ...prev,
          designsLeft: Math.max(0, prev.designsLeft - 1),
          canDesign: prev.designsLeft > 1
        } : null)
      }

      // Step 6: Show the comparison view
      setIsGenerating(false)
      setShowComparison(true)
      // Clear the message interval
      clearInterval(messageInterval)

      const autoSaveDesign = async () => {
        // Generate a default name based on the number of saved designs
        const designNumber = savedDesigns.length + 1;
        const defaultName = `Design ${designNumber}`;

        // Auto-save with default name
        await handleSaveDesign({
          generatedImageUrl: renovationResult.result_image_url,
          originalImageUrl: originalImageUrl,
          name: defaultName,
          id: `auto-save-${Date.now()}`,
          timestamp: Date.now()
        });
      };

      autoSaveDesign();
    } catch (error) {
      console.error("Failed to generate design:", error)
      setGenerationError(error instanceof Error ? error.message : "An unknown error occurred")
      setIsGenerating(false)
      // Clear the message interval on error
      clearInterval(messageInterval)
    }
  }

  // Reset to bubble view
  const handleResetView = () => {
    setShowComparison(false)
    setActiveDesignId(null)
  }



  // Handle saving a design
  const handleSaveDesign = async (savedDesign: SavedDesign) => {
    try {
      // Extract just the IDs from preservedObjects for the API
      const preservedObjectIds = preservedObjects.map(obj => obj.originalObject);

      // First, try to save the design to the server
      const result = await saveDesign(
        savedDesign.name,
        uploadedAsset?.id || "",
        savedDesign.originalImageUrl,
        savedDesign.generatedImageUrl,
        preservedObjectIds,
        bubbleStyles,
        user_id,
        effectiveApiKey,
        extra, // Pass the extra parameters to save API
        savedDesign.id // Pass the renovation ID if available
      );

      if (result.success) {
        const newDesign: SavedDesign = {
          id: result.renovation_id || `design-${Date.now()}`,
          name: savedDesign.name,
          originalImageUrl: savedDesign.originalImageUrl,
          generatedImageUrl: savedDesign.generatedImageUrl,
          timestamp: Date.now()
        };

        setSavedDesigns(prevDesigns => {
          const existingDesignIndex = prevDesigns.findIndex(d => d.id === newDesign.id);

          if (existingDesignIndex > -1) {
            // Update existing design
            const updatedDesigns = [...prevDesigns];
            updatedDesigns[existingDesignIndex] = newDesign;
            return updatedDesigns;
          } else {
            // Add new design
            return [...prevDesigns, newDesign];
          }
        });
        setActiveDesignId(newDesign.id);

        // Notify the parent window that a design was saved
        window.DzinlyWidget.sendMessage({
          type: 'design_saved',
          data: {
            designId: newDesign.id,
            name: newDesign.name
          }
        });

      } else {
        throw new Error('Failed to save design: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {

      // Show error message via toast
      toast.error(`Could not save design to server. Please contact support if you continue to experience issues.`, {
        description: 'Your design will be available during this session.',
        duration: 5000
      });
    }
  }

  // Handle design deletion
  const handleDeleteDesign = async (designId: string) => {
    try {
      // Find the design to delete
      const designToDelete = savedDesigns.find(d => d.id === designId);
      if (!designToDelete) return;

      // Try to delete from server
      await deleteDesign(designId, user_id, effectiveApiKey);

      // Remove from local state
      setSavedDesigns(savedDesigns.filter(d => d.id !== designId));

      // If the active design was deleted, reset to bubble view
      if (activeDesignId === designId) {
        setActiveDesignId(null);
        setShowComparison(false);
      }

      // Show success message
      toast.success(`Design deleted successfully`, {
        duration: 3000
      });
    } catch (error) {
      console.error('Error deleting design:', error);

      // Fallback: Remove from local state even if server delete fails
      setSavedDesigns(savedDesigns.filter(d => d.id !== designId));

      // If the active design was deleted, reset to bubble view
      if (activeDesignId === designId) {
        setActiveDesignId(null);
        setShowComparison(false);
      }

      // Show error message
      toast.error(`Could not delete design from server. Removed locally.`, {
        duration: 3000
      });
    }
  }

  // Handle renaming a design
  const handleRenameDesign = (designId: string) => {
    // Find the design to rename
    const designToRename = savedDesigns.find(d => d.id === designId);
    console.log('designToRename', designToRename);
    if (!designToRename) return;

    // Set as active design
    setActiveDesignId(designId);

    // Open the save design modal for renaming
    setIsSaveDesignModalOpen(true);
  }



  // Handle switching to a saved design or original
  const handleSwitchDesign = (designId: string | null) => {
    // If we're switching to Original Design and we're in bubble view
    if (designId === null && !showComparison) {
      // Show comparison view for Original Design
      setShowComparison(true)
      setActiveDesignId(null)
    }
    // If we're switching to Original Design from a saved design
    else if (designId === null && activeDesignId !== null) {
      // Go back to bubble view
      setShowComparison(false)
      setActiveDesignId(null)
    }
    // If we're switching to a saved design
    else if (designId !== null) {
      // Always show comparison view for saved designs
      setShowComparison(true)
      setActiveDesignId(designId)
    }
  }

  // Send message to parent window when design is generated
  useEffect(() => {
    if (showComparison) {
      window.DzinlyWidget.sendMessage({
        type: 'design_generated',
        data: {
          designsLeft: creditDetails?.designsLeft || 0,
          canDesign: creditDetails?.canDesign || false
        }
      });
    }
  }, [showComparison, creditDetails]);

  // Handle "Buy More Credits" action
  const handleBuyMoreCredits = () => {
    // Send message to parent window
    window.DzinlyWidget.sendMessage({
      type: 'buy_more_credits'
    });
  };


  // If mandatory parameters are missing, show error message instead of widget
  if (missingParams.length > 0) {
    return <MissingParametersError missingParams={missingParams} />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-0 dzinly-widget-container">
      <div className="bg-white w-full h-full" ref={widgetRef}>
        <div className="flex flex-col md:flex-row h-full">
          {/* Main Content Area */}
          <div className="flex-1 p-4 flex flex-col" ref={containerRef}>
            {/* Tab Header - Display the photo angle from URL parameters */}
            <div className="bg-dzinly-primary text-white px-4 py-2 inline-block rounded-t-md mb-0">
              {photo_angle === 'front_view' ? 'Front' :
               photo_angle === 'rear_view' ? 'Rear' :
               photo_angle === 'left_view' ? 'Left' :
               photo_angle === 'right_view' ? 'Right' : 'Front'}
            </div>

            {/* Visualization Area - Conditional rendering based on state */}
            {isGenerating ? (
              <div className="bg-gray-100 h-[500px] relative rounded-tr-md rounded-b-md overflow-hidden flex flex-col items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                  <Loader2 className="h-12 w-12 animate-spin text-dzinly-primary" />
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-800">Generating Your Design</p>
                    <div className="relative text-sm text-gray-600 mt-1 min-h-[48px] px-4">
                      <p
                        key={loadingMessageIndex}
                        className="animate-fadeIn absolute left-0 right-0 text-center"
                      >
                        {loadingMessages[loadingMessageIndex]}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : showComparison ? (
              <div className="bg-gray-100 h-[500px] relative rounded-tr-md rounded-b-md overflow-hidden flex items-center justify-center">
                <div className="max-w-full max-h-full w-full h-full flex items-center justify-center">
                  <ReactCompareSlider
                    itemOne={
                      <ReactCompareSliderImage
                        src={activeDesignId
                          ? savedDesigns.find(d => d.id === activeDesignId)?.originalImageUrl || originalImageUrl
                          : originalImageUrl}
                        alt="Original Design"
                        className="object-contain"
                        style={{ maxHeight: "500px", maxWidth: "100%" }}
                      />
                    }
                    itemTwo={
                      <ReactCompareSliderImage
                        src={activeDesignId
                          ? savedDesigns.find(d => d.id === activeDesignId)?.generatedImageUrl || generatedImageUrl
                          : generatedImageUrl || "/placeholder.svg?height=500&width=500"}
                        alt="Generated Design"
                        className="object-contain"
                        style={{ maxHeight: "500px", maxWidth: "100%" }}
                        onError={(e) => {
                          console.error("Failed to load generated image");
                          e.currentTarget.src = "/placeholder.svg?height=500&width=500";
                        }}
                      />
                    }
                    position={50}
                    className="max-h-[500px]"
                    style={{
                      maxHeight: '500px',
                      width: 'auto'
                    }}
                  />
                </div>
              </div>
            ) : (
              <BubbleVisualizer
                centerBubble={centerBubble}
                bubbleStyles={bubbleStyles}
                onAddStyle={handleAddStyle}
                onRemoveBubble={handleRemoveBubble}
                isModalOpen={isStyleModalOpen}
                isGenerating={false}
              />
            )}

            {/* Generation Error Message */}
            {generationError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p className="text-sm">Error generating design: {generationError}</p>
                <p className="text-xs mt-1">Please try again or contact support if the problem persists.</p>
              </div>
            )}

            {/* Bottom Controls */}
            <div className="flex items-center mt-4">
              {/* Design Selection Tabs */}
              {(showComparison || savedDesigns.length > 0) && (
                <div className="flex items-center flex-wrap gap-2 max-w-[100%]">
                  {savedDesigns.map((design) => (
                    <div
                      key={design.id}
                      className={`px-3 py-2 text-sm whitespace-nowrap rounded-md flex items-center justify-between gap-2 ${
                        activeDesignId === design.id
                          ? "bg-gray-100 border border-dzinly-primary"
                          : "bg-gray-50 border border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <button
                        className="font-medium"
                        onClick={() => handleSwitchDesign(design.id)}
                      >
                        {design.name}
                      </button>
                      <DesignOptionsMenu
                        designId={design.id}
                        designName={design.name}
                        generatedImageUrl={design.generatedImageUrl}
                        onRename={handleRenameDesign}
                        onDelete={handleDeleteDesign}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="ml-auto">
                {showComparison ? (
                  <Button
                    variant="ghost"
                    className="text-gray-600"
                    onClick={handleResetView}
                    size="sm"
                  >
                    Back to Editor
                  </Button>
                ) : generatedImageUrl && (
                  <Button
                    variant="ghost"
                    className="text-dzinly-primary hover:text-dzinly-secondary"
                    onClick={() => {
                      setShowComparison(true);
                      const lastDesign = savedDesigns?.length
                        ? savedDesigns[savedDesigns.length - 1]
                        : null;
                      if(lastDesign){
                        handleSwitchDesign(lastDesign?.id || null);
                      }
                    }}
                    size="sm"
                  >
                    View Last Design
                  </Button>
                )}
              </div>
            </div>


          </div>

          {/* Right Sidebar */}
          <div className="w-full md:w-80 bg-white border border-gray-200 rounded-md overflow-hidden flex flex-col my-4 mr-4 shadow-sm">
            {/* Custom scrollable container */}
            {/* AI Design Header - Updated design */}
            <div className="bg-dzinly-primary text-white px-4 py-3 rounded-t-md flex items-center justify-between gap-5">
              <a
                href="https://renovateai.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center hover:opacity-90 transition-opacity"
                aria-label="Visit Renovate AI"
              >
                <div className="flex items-center">
                  <img
                    src="/renovate-ai-logo-text.png"
                    alt="AI Design POWERED BY RENOVATE AI"
                    className="h-16 w-auto object-contain"
                  />
                </div>
              </a>
              <Button
                className="bg-white text-dzinly-primary hover:bg-gray-100 py-2.5 px-5 rounded-md shadow-sm font-medium text-base whitespace-nowrap flex-shrink-0 min-w-[120px] flex justify-center"
                onClick={()=>{
                  handleGenerate();
                }}
                disabled={isGenerating || isUploadingAsset || (creditDetails?.designsLeft === 0 && !showComparison)}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  </>
                ) : isUploadingAsset ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Preparing
                  </>
                ) : (
                  "Generate"
                )}
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">

            {/* Design Credit Status */}
            {creditDetails && (
              <div className="text-center py-2 px-4 border-b">
                <p className="text-sm font-medium">
                  {(creditDetails?.designsLeft || 0)}/{(creditDetails?.totalDesigns || 0)} Designs Left
                </p>
                  <Button
                    className="bg-dzinly-primary hover:bg-dzinly-secondary mt-2 text-xs"
                    size="sm"
                    onClick={handleBuyMoreCredits}
                  >
                    Buy More Credits
                  </Button>
              </div>
            )}

            {/* Architecture Style */}
            {/* <div className="pt-3 pb-1 px-4 border-b-0">
              <h3 className="font-medium mb-2">Architecture Style</h3>
              {isLoadingConfig ? (
                <div className="flex items-center justify-center h-20">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin text-dzinly-primary" />
                  <span className="text-sm text-gray-600">Loading styles...</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <div
                    className="w-16 h-16 bg-gray-100 border rounded-md flex flex-col items-center justify-center text-xs cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleOpenStyleModal("architecture")}
                  >
                    <Plus size={16} className="mb-1" />
                    <span>Add</span>
                    <span>Style</span>
                  </div>
                  {bubbleStyles
                    .filter((style) => style.type === "architecture")
                    .slice(0, 3)
                    .map((style) => (
                      <div key={style.id} className="w-16 text-center">
                        <div className="w-16 h-16 border rounded-md overflow-hidden">
                          <img
                            src={style.architectureData?.preview_image || style.image || "/placeholder.svg"}
                            alt={style.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = "/placeholder.svg?height=80&width=80"
                            }}
                          />
                        </div>
                        <span className="text-xs truncate block w-16 overflow-hidden text-center mt-1">{style.name}</span>
                      </div>
                    ))}
                </div>
              )}
            </div> */}


            {/* Inspirational Images */}
            <div className="py-3 px-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Inspirational Images</h3>
                <button
                  className="text-xs text-dzinly-primary hover:text-dzinly-secondary transition-colors"
                  onClick={() => setIsInspirationModalOpen(true)}
                >
                  See All
                </button>
              </div>
              
              {/* Category filter dropdown */}
              {widgetColorFamiliesWithHouses && widgetColorFamiliesWithHouses.length > 0 && (
                <div className="relative mb-3" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between text-sm transition-colors"
                  >
                    <span className="text-gray-700">{getSelectedFamilyName()}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedColorFamily('all')
                          setIsDropdownOpen(false)
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          selectedColorFamily === 'all' ? 'bg-dzinly-primary/10 text-dzinly-primary font-medium' : 'text-gray-700'
                        }`}
                      >
                        All Categories
                      </button>
                      {widgetColorFamiliesWithHouses.map((family) => (
                        <button
                          key={family.code}
                          onClick={() => {
                            setSelectedColorFamily(family.code)
                            setIsDropdownOpen(false)
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                            selectedColorFamily === family.code ? 'bg-dzinly-primary/10 text-dzinly-primary font-medium' : 'text-gray-700'
                          }`}
                        >
                          {family.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {isLoadingConfig ? (
                <div className="flex items-center justify-center h-20">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin text-dzinly-primary" />
                  <span className="text-sm text-gray-600">Loading inspirational images...</span>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {/* Add More button */}
                  <div
                    className="aspect-square bg-gray-100 border border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setIsInspirationModalOpen(true)}
                  >
                    <Plus size={16} className="mb-0.5 text-gray-600" />
                    <span className="text-xs text-gray-600">Add</span>
                    <span className="text-xs text-gray-600">More</span>
                  </div>

                  {/* Show filtered inspiration images based on selected category */}
                  {(() => {
                    const allInspirationImages: InspirationImage[] = [];
                    
                    // Create a stable list of all available images from color families first
                    const colorFamilyImages: InspirationImage[] = [];
                    if (widgetColorFamiliesWithHouses && widgetColorFamiliesWithHouses.length > 0) {
                      if (selectedColorFamily === 'all') {
                        // Show all images from all families
                        widgetColorFamiliesWithHouses.forEach(family => {
                          family.inspirational_houses.forEach(house => {
                            colorFamilyImages.push({
                              id: house.code,
                              name: house.name,
                              url: house.image
                            });
                          });
                        });
                      } else {
                        // Show only images from selected family
                        const selectedFamily = widgetColorFamiliesWithHouses.find(f => f.code === selectedColorFamily);
                        if (selectedFamily) {
                          selectedFamily.inspirational_houses.forEach(house => {
                            colorFamilyImages.push({
                              id: house.code,
                              name: house.name,
                              url: house.image
                            });
                          });
                        }
                      }
                    }
                    
                    // Add custom uploaded images first (only those not in color families)
                    inspirationCollection.forEach(inspiration => {
                      if (!colorFamilyImages.some(img => img.id === inspiration.id)) {
                        allInspirationImages.push(inspiration);
                      }
                    });
                    
                    // Then add color family images in their stable order
                    allInspirationImages.push(...colorFamilyImages);
                    
                    // Show all filtered images
                    return allInspirationImages.map((image) => (
                      <div
                        key={image.id}
                        className="relative cursor-pointer group"
                        onClick={() => {
                          // Add to collection if it's a custom image not already there
                          if (!inspirationCollection.some(i => i.id === image.id)) {
                            // Check if this is a custom image (not from color families)
                            const isColorFamilyImage = widgetColorFamiliesWithHouses.some(family => 
                              family.inspirational_houses.some(house => house.code === image.id)
                            );
                            
                            if (!isColorFamilyImage) {
                              setInspirationCollection(prev => [...prev, image]);
                            }
                          }
                          handleSelectInspirationFromCollection(image.id);
                        }}
                      >
                        <div className="aspect-square border border-gray-300 overflow-hidden rounded-lg relative">
                          <img
                            src={image.url || "/placeholder.svg"}
                            alt={image.name || "Inspiration"}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            onError={(e) => {
                              e.currentTarget.src = "/placeholder.svg?height=100&width=100"
                            }}
                          />
                          
                          {/* Checkmark for selected image */}
                          {selectedInspirationId === image.id && (
                            <div className="absolute top-1 right-1 bg-dzinly-primary text-white rounded-full p-0.5">
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Keyword Input - DISABLED
            <div className="py-3 px-4 border-b-0">
              <div className="h-0.5 bg-dzinly-primary -mx-4 mb-3"></div>
              <h3 className="font-medium mb-2">Keyword Input</h3>
              <p className="text-xs text-gray-500 mb-3">Example: 'Dark Tones' 'Roof Only'</p>
              <Button
                className="w-full bg-dzinly-primary hover:bg-dzinly-secondary py-3 text-white font-medium"
                onClick={() => setIsKeywordModalOpen(true)}
              >
                Enter Keyword
              </Button>
            </div>
            */}

            {/* Preserve Objects - DISABLED
            <div className="py-3 px-4">
              <div className="h-0.5 bg-dzinly-primary -mx-4 mb-3"></div>
              <h3 className="font-medium mb-2">Preserve Elements</h3>
              <p className="text-xs text-gray-500 mb-3">If you want to preserve some parts of the original image, you can scan the objects and preserve the original image.</p>
              <Button
                className="w-full bg-dzinly-primary hover:bg-dzinly-secondary py-3 text-white font-medium"
                onClick={() => setIsPreserveObjectsModalOpen(true)}
              >
                Preserve Elements
              </Button>
              {preservedObjects.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium mb-2">Selected objects to preserve:</p>
                  <div className="flex flex-wrap gap-1">
                    {preservedObjects.map(obj => (
                      <span
                        key={obj.originalObject}
                        className="text-xs bg-dzinly-light text-dzinly-primary px-2 py-1 rounded-full flex items-center"
                      >
                        <span>
                          {obj.name}
                        </span>
                        <button
                          className="ml-1 rounded-full w-4 h-4 flex items-center justify-center bg-dzinly-primary text-white text-xs hover:bg-dzinly-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreservedObjects(prev => prev.filter(o => o.originalObject !== obj.originalObject));
                          }}
                          aria-label={`Remove ${obj.name}`}
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>

                  {preservedObjects.length >= 4 && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-md">
                      <p>⚠️ You are preserving many objects. AI renovation capabilities may be limited.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            */}

            </div> {/* End of scrollable container */}
          </div>
        </div>
      </div>

      {/* Architecture Style Modal - Lazily loaded */}
      <Suspense fallback={null}>
        {isStyleModalOpen && (
          <ArchitectureStyleModal
            open={isStyleModalOpen}
            onOpenChange={setIsStyleModalOpen}
            onSelectStyle={handleSelectStyle}
            containerRef={widgetRef}
            // Convert dynamic styles to compatible format
            styles={widgetStyles.length > 0 ? widgetStyles.map(style => ({
              id: style.code,
              name: style.name,
              preview_image: style.image
            })) : undefined}
          />
        )}
      </Suspense>


      {/* Inspiration Images Modal - Lazily loaded */}
      <Suspense fallback={null}>
        {isInspirationModalOpen && (
          <InspirationImagesModal
            open={isInspirationModalOpen}
            onOpenChange={setIsInspirationModalOpen}
            onSelectImage={handleSelectInspirationImage}
            containerRef={widgetRef}
            // Pass color families with houses data to the modal
            colorFamiliesWithHouses={colorFamiliesWithHouses}
            // Convert dynamic inspirational houses to compatible format
            inspirationImages={widgetInspirationHouses.length > 0 ? widgetInspirationHouses.map(house => ({
              id: house.code,
              name: house.name,
              url: house.image
            })) : undefined}
          />
        )}
      </Suspense>

      {/* Keyword Modal - Lazily loaded */}
      <Suspense fallback={null}>
        {isKeywordModalOpen && (
          <KeywordModal
            open={isKeywordModalOpen}
            onOpenChange={setIsKeywordModalOpen}
            onSave={handleAddKeyword}
            containerRef={widgetRef}
          />
        )}
      </Suspense>

      {/* Save Design Modal - Lazily loaded */}
      <Suspense fallback={null}>
        {isSaveDesignModalOpen && activeDesignId && (
          <SaveDesignModal
            open={isSaveDesignModalOpen}
            onOpenChange={setIsSaveDesignModalOpen}
            onSave={(name: string)=> {
              handleSaveDesign({
                generatedImageUrl: savedDesigns.find(d => d.id === activeDesignId)?.generatedImageUrl || generatedImageUrl,
                originalImageUrl: originalImageUrl,
                name: name || savedDesigns.find(d => d.id === activeDesignId)?.name || "My Design",
                id: activeDesignId,
                timestamp: Date.now()
              });
            }}
            containerRef={widgetRef}
            originalImageUrl={activeDesignId
              ? savedDesigns.find(d => d.id === activeDesignId)?.originalImageUrl || originalImageUrl
              : originalImageUrl}
            generatedImageUrl={activeDesignId
              ? savedDesigns.find(d => d.id === activeDesignId)?.generatedImageUrl || generatedImageUrl
              : generatedImageUrl}
            defaultName={activeDesignId
              ? savedDesigns.find(d => d.id === activeDesignId)?.name || "My Design"
              : "My Design"}
          />
        )}
      </Suspense>

      {/* Preserve Objects Modal - Lazily loaded */}
      <Suspense fallback={null}>
        {isPreserveObjectsModalOpen && (
          <PreserveObjectsModal
            open={isPreserveObjectsModalOpen}
            onOpenChange={setIsPreserveObjectsModalOpen}
            onSave={handleSavePreservedObjects}
            imageUrl={originalImageUrl}
            containerRef={widgetRef}
          />
        )}
      </Suspense>
    </div>
  )
}